import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress, Typography } from '@mui/material';

export const STALL_MS = 60000;

const reportsLabel = (count) => `${count} ${count === 1 ? 'report' : 'reports'}`;

export const timeLeftLabel = ({ startedAt, completed, total }, now) => {
    const elapsed = now - startedAt;
    if (completed < 5 || elapsed < 2000) return null;
    const seconds = Math.ceil(((elapsed / completed) * (total - completed)) / 1000);
    if (seconds <= 5) return 'Almost done';
    if (seconds < 60) return `About ${Math.ceil(seconds / 10) * 10} seconds left`;
    const minutes = Math.ceil(seconds / 60);
    return `About ${minutes} ${minutes === 1 ? 'minute' : 'minutes'} left`;
};

const stageLabel = (state) => {
    if (state.stage === 'starting') return 'Starting your download';
    return `Collecting report ${Math.min(state.completed + 1, state.total)} of ${state.total}`;
};

const stageCaption = (state, timeLeft, stalled) => {
    if (stalled) return "This is taking longer than expected. If the file is no longer in your browser's downloads, cancel and try again.";
    if (state.stage === 'starting') return 'Your browser will show the file in its downloads bar in a moment.';
    return timeLeft ?? `Your browser is downloading the file while the ${reportsLabel(state.total)} are collected. Large downloads can take a few minutes.`;
};

const RunningContent = ({ state }) => {
    const [now, setNow] = useState(() => Date.now());
    const [changedAt, setChangedAt] = useState(() => Date.now());
    useEffect(() => {
        const timer = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(timer);
    }, []);
    useEffect(() => {
        setChangedAt(Date.now());
    }, [state.stage, state.completed]);
    const collecting = state.stage === 'collecting';
    const stalled = now - changedAt >= STALL_MS;
    const percent = state.total > 0 ? Math.round((state.completed / state.total) * 100) : 0;
    const timeLeft = collecting && !stalled ? timeLeftLabel(state, now) : null;

    return (
        <>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {stageLabel(state)}
            </Typography>
            <LinearProgress variant={collecting ? 'determinate' : 'indeterminate'} value={collecting ? percent : undefined} sx={{ my: 2, height: 8, borderRadius: 4 }} />
            <Typography variant="body2" color={stalled ? 'warning.main' : 'text.secondary'}>
                {stageCaption(state, timeLeft, stalled)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Keep this page open until the download finishes.
            </Typography>
        </>
    );
};

const MAX_LISTED_SKIPPED = 10;

const skippedList = (skipped) => {
    const shown = skipped.slice(0, MAX_LISTED_SKIPPED).join(', ');
    const more = skipped.length - MAX_LISTED_SKIPPED;
    return more > 0 ? `${shown} and ${more} more` : shown;
};

const DoneContent = ({ state }) => (
    <>
        <Typography variant="body1">
            {reportsLabel(state.downloaded)} downloaded as <strong>{state.filename}</strong>.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Look for it in your browser&apos;s downloads.
        </Typography>
        {state.skipped.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
                {reportsLabel(state.skipped.length)} could not be included: {skippedList(state.skipped)}. Download again, or regenerate {state.skipped.length === 1 ? 'that report' : 'those reports'} if the problem persists.
            </Alert>
        )}
    </>
);

const LostContent = ({ state }) => (
    <>
        <Typography variant="body1">{state.error}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            If the file is there, you are done. If it is not, start the download again.
        </Typography>
    </>
);

const TITLES = { running: 'Preparing your download', done: 'Your file is ready', failed: 'Download failed', lost: 'Lost track of your download' };

const DeliveryReportsDownloadDialog = ({ state, onRetry, onCancel, onClose }) => {
    const open = state.status !== 'idle';

    return (
        <Dialog open={open} onClose={state.status === 'running' ? undefined : onClose} aria-labelledby="delivery-reports-download-title" maxWidth="xs" fullWidth>
            <DialogTitle id="delivery-reports-download-title">{TITLES[state.status] ?? ''}</DialogTitle>
            <DialogContent>
                {state.status === 'running' && <RunningContent state={state} />}
                {state.status === 'done' && <DoneContent state={state} />}
                {state.status === 'lost' && <LostContent state={state} />}
                {state.status === 'failed' && (
                    <Box>
                        <Alert severity="error">{state.error}</Alert>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                {state.status === 'running' && <Button onClick={onCancel}>Cancel</Button>}
                {state.status === 'done' && (
                    <Button onClick={onClose} variant="contained">
                        Done
                    </Button>
                )}
                {state.status === 'failed' && (
                    <>
                        <Button onClick={onClose}>Close</Button>
                        <Button onClick={onRetry} variant="contained">
                            Retry
                        </Button>
                    </>
                )}
                {state.status === 'lost' && (
                    <>
                        <Button onClick={onClose}>Close</Button>
                        <Button onClick={onRetry} variant="contained">
                            Download again
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default DeliveryReportsDownloadDialog;
